import { Box } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { JSX } from "react";

import type { ScheduledPost } from "../../types.js";
import { dayKey } from "../calendar-utils.js";
import { CalendarChip } from "./CalendarChip.js";
import { CHIPS_PER_CELL, WEEKDAYS } from "./scheduleHelpers.js";

/**
 * The month grid — a weekday header + a stable 6-week matrix of day cells. Each
 * cell shows its date, up to `CHIPS_PER_CELL` post chips, and a "+N more"
 * overflow; clicking (or Enter/Space) selects the day. Extracted from
 * `ScheduleCalendar.tsx`; the parent owns the cursor + selection state.
 */
export function MonthGrid({
  weeks,
  byDay,
  cursorMonth,
  todayKey,
  selectedKey,
  onSelectDay,
}: {
  weeks: Date[][];
  byDay: Map<string, ScheduledPost[]>;
  cursorMonth: number;
  todayKey: string;
  selectedKey: string;
  onSelectDay: (key: string) => void;
}): JSX.Element {
  return (
    <>
      {/* Weekday header */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0.5 }}>
        {WEEKDAYS.map((w) => (
          <Box
            key={w}
            sx={{
              fontSize: 10,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              color: "text.secondary",
              textAlign: "center",
              py: 0.5,
            }}
          >
            {w}
          </Box>
        ))}
      </Box>

      {/* Month grid */}
      <Box sx={{ display: "grid", gap: 0.5 }}>
        {weeks.map((week, wi) => (
          <Box key={wi} sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0.5 }}>
            {week.map((d) => {
              const key = dayKey(d);
              const posts = byDay.get(key) ?? [];
              const inMonth = d.getMonth() === cursorMonth;
              const isToday = key === todayKey;
              const isSelected = key === selectedKey;
              return (
                <Box
                  key={key}
                  role="button"
                  tabIndex={0}
                  data-testid={`cal-day-${key}`}
                  onClick={() => onSelectDay(key)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onSelectDay(key);
                    }
                  }}
                  sx={{
                    minHeight: 72,
                    p: 0.5,
                    borderRadius: 1,
                    border: 1,
                    borderColor: isSelected ? "primary.main" : "divider",
                    bgcolor: isSelected
                      ? (t) => alpha(t.palette.primary.main, 0.06)
                      : inMonth
                        ? "background.paper"
                        : (t) => alpha(t.palette.text.secondary, 0.03),
                    cursor: "pointer",
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.25,
                    transition: "background-color 120ms",
                    "&:hover": { bgcolor: "action.hover" },
                    "&:focus-visible": {
                      outline: (t) => `2px solid ${t.palette.primary.main}`,
                      outlineOffset: -1,
                    },
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      alignSelf: "flex-start",
                      fontSize: 11,
                      fontWeight: isToday ? 700 : 500,
                      color: isToday
                        ? "primary.contrastText"
                        : inMonth
                          ? "text.primary"
                          : "text.disabled",
                      ...(isToday
                        ? {
                            bgcolor: "primary.main",
                            borderRadius: 999,
                            px: 0.625,
                            minWidth: 18,
                            textAlign: "center",
                          }
                        : { px: 0.25 }),
                    }}
                  >
                    {d.getDate()}
                  </Box>
                  {posts.slice(0, CHIPS_PER_CELL).map((p) => (
                    <CalendarChip key={p.id} post={p} />
                  ))}
                  {posts.length > CHIPS_PER_CELL && (
                    <Box component="span" sx={{ fontSize: 10, color: "text.secondary", px: 0.25 }}>
                      +{posts.length - CHIPS_PER_CELL} more
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>
    </>
  );
}
